import { Card, Metric, Text, Flex, BadgeDelta, Grid } from '@tremor/react';
import currency from 'currency.js';
import Link from 'next/link';
import { Fragment, useCallback } from 'react';

export default function DashboardKpiCards({
  sellerPerformance,
  myPurchasesCount,
  stripeSellerDashboardSSOLink,
}: {
  sellerPerformance?: {
    currentMonthTotal: number;
    currentMonthSales: number;
    currentMonthBuyers: number;
    lastMonthTotal: number;
    lastMonthSales: number;
    lastMonthBuyers: number;
  };
  myPurchasesCount?: number;
  stripeSellerDashboardSSOLink: string;
}) {
  const sellerPerformanceDeltaCalc = useCallback(
    (type: 'Total' | 'Sales' | 'Buyers') => {
      const currentMonthValue = sellerPerformance?.[`currentMonth${type}`] || 0;
      const lastMonthValue = sellerPerformance?.[`lastMonth${type}`] || 0;
      if (currentMonthValue === 0 && lastMonthValue === 0) {
        return 0;
      }
      return (
        ((currentMonthValue - lastMonthValue) / (lastMonthValue || 1)) * 100
      );
    },
    [sellerPerformance],
  );

  const sellerDeltaFn = useCallback((delta) => {
    if (delta > 0) {
      return 'moderateIncrease';
    } else if (delta === 0) {
      return 'unchanged';
    } else {
      return 'moderateDecrease';
    }
  }, []);

  const totalDelta = sellerPerformanceDeltaCalc('Total');
  const salesDelta = sellerPerformanceDeltaCalc('Sales');
  const buyerDelta = sellerPerformanceDeltaCalc('Buyers');

  const categories = sellerPerformance
    ? [
        {
          title: 'Rentals',
          metric: sellerPerformance?.currentMonthSales.toString(),
          metricPrev: sellerPerformance?.lastMonthSales.toString(),
          delta: salesDelta.toFixed(2) + '%',
          deltaType: sellerDeltaFn(salesDelta),
          href: '/dashboard/transaction/sales',
        },
        {
          title: 'Amount Made',
          metric: currency(sellerPerformance?.currentMonthTotal, {
            fromCents: true,
            symbol: '$',
          }).format(),
          metricPrev: currency(sellerPerformance?.lastMonthTotal, {
            fromCents: true,
            symbol: '$',
          }).format(),
          delta: totalDelta.toFixed(2) + '%',
          deltaType: sellerDeltaFn(totalDelta),
          href: stripeSellerDashboardSSOLink,
        },
        {
          title: 'Unique Buyers',
          metric: sellerPerformance?.currentMonthBuyers.toString(),
          metricPrev: sellerPerformance.lastMonthBuyers.toString(),
          delta: buyerDelta.toFixed(2) + '%',
          deltaType: sellerDeltaFn(buyerDelta),
          href: null,
        },
      ]
    : [
        {
          title: 'Total Purchases',
          metric: myPurchasesCount,
          delta: salesDelta.toFixed(2) + '%',
          deltaType: sellerDeltaFn(salesDelta),
          href: '/dashboard/transaction/sales',
        },
      ];

  return (
    <Grid numItemsSm={2} numItemsLg={3} className="gap-6">
      {categories.map((item) => {
        const El = item.href ? Link : Fragment;
        const props = item.href ? { href: item.href } : {};
        return (
          <El {...props}>
            <Card key={item.title}>
              <Flex alignItems="start">
                <Text>{item.title}</Text>
                <BadgeDelta deltaType={item.deltaType}>{item.delta}</BadgeDelta>
              </Flex>
              <Flex
                justifyContent="start"
                alignItems="baseline"
                className="truncate space-x-3"
              >
                <Metric>{item.metric}</Metric>
                {item.metricPrev && (
                  <Text className="truncate">
                    from {item.metricPrev}{' '}
                    <span className="text-xs">last month</span>
                  </Text>
                )}
              </Flex>
            </Card>
          </El>
        );
      })}
    </Grid>
  );
}
